import { useEffect } from "react";
import { soundApi } from "../soundDetail/soundApi";
import { isSignedIn } from "../fanHome/fanHomeApi";

// Tells the server a post made with a catalog sound was really watched or listened to, so the artist earns royalties for it.
// 1) When playback starts the server gives a signed ticket (soundApi.videoViewStart); it answers none when the view could never count
//    (muted, the viewer's own post, ...), and then nothing more is sent.
// 2) After 5 seconds of real playing (seeking, pausing and looping add no time) the view is reported once with that ticket and the seconds
//    played. The server refuses a report that claims more time than has passed since the ticket was issued, and decides whether the view
//    counts (paying member, not the poster, not the sound's artist, ...). A failed call never disturbs the player.
// `mediaRef` is the video or audio element; `key` restarts the count when the source changes.
export default function useSoundViewReport(mediaRef, { postId, enabled, key }) {
  useEffect(() => {
    const el = mediaRef.current;
    if (!el || !postId || !enabled || !isSignedIn()) return undefined;
    let played = 0;
    let last = null;
    let reported = false;
    let ticket = null;
    let asked = false;
    let declined = false;
    const ask = () => {
      if (asked || declined) return;
      asked = true;
      soundApi.videoViewStart(postId).then(
        (answer) => { ticket = answer?.ticket || null; if (!ticket) declined = true; },
        () => { asked = false; } // try again on the next play
      );
    };
    const onPlay = () => { last = null; ask(); };
    const onTime = () => {
      if (el.paused || el.seeking || declined) { last = null; return; }
      const t = el.currentTime;
      if (last !== null) {
        const step = t - last;
        if (step > 0 && step < 2) played += step;
      }
      last = t;
      if (!reported && played >= 5) {
        if (!ticket) { ask(); return; }
        reported = true;
        soundApi.videoView(postId, Math.floor(played), ticket).catch(() => { /* ignored */ });
      }
    };
    const reset = () => { last = null; };
    el.addEventListener("play", onPlay);
    el.addEventListener("timeupdate", onTime);
    el.addEventListener("pause", reset);
    el.addEventListener("seeking", reset);
    el.addEventListener("ended", reset);
    if (!el.paused) ask();
    return () => {
      el.removeEventListener("play", onPlay);
      el.removeEventListener("timeupdate", onTime);
      el.removeEventListener("pause", reset);
      el.removeEventListener("seeking", reset);
      el.removeEventListener("ended", reset);
    };
  }, [mediaRef, postId, enabled, key]);
}
